import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

/**
 * Customer data pack export — one click produces a CSV emissions summary
 * formatted for a reporting entity's Scope 3 data request.
 */
const DataPack: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState<number>(currentYear);
	const [downloading, setDownloading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

	const handleDownload = async () => {
		setDownloading(true);
		setError(null);
		try {
			const token = await getAccessTokenSilently();
			const response = await fetch(
				`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/data-pack/export`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ year }),
				},
			);

			if (!response.ok) {
				let message = "Could not build the data pack. Try again.";
				try {
					const data = await response.json();
					message = data.Message || data.message || message;
				} catch {
					// non-JSON error body; keep default message
				}
				setError(message);
				return;
			}

			const blob = await response.blob();
			const disposition =
				response.headers.get("Content-Disposition") || "";
			const match = disposition.match(/filename="?([^";]+)"?/);
			const filename = match?.[1] || `emissions_data_pack_${year}.csv`;

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (e: any) {
			setError(
				"Could not build the data pack. Check your connection and try again.",
			);
		} finally {
			setDownloading(false);
		}
	};

	return (
		<Box display="flex" justifyContent="center" padding={3}>
			<Paper
				elevation={0}
				sx={{
					maxWidth: 560,
					width: "100%",
					padding: "32px",
					border: "1px solid rgba(0, 23, 28, 0.10)",
					borderRadius: "12px",
				}}
			>
				<Typography variant="h5" gutterBottom>
					Customer data pack
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ marginBottom: 3 }}
				>
					Download a summary of your emissions for a reporting year,
					formatted to send to a customer that has requested your
					emissions data. It includes your Scope totals by category in
					tonnes CO2e, with method notes.
				</Typography>

				<Box display="flex" gap={2} alignItems="center">
					<TextField
						select
						label="Reporting year"
						value={year}
						onChange={(e) => setYear(Number(e.target.value))}
						size="small"
						sx={{ minWidth: 160 }}
					>
						{yearOptions.map((y) => (
							<MenuItem key={y} value={y}>
								{y}
							</MenuItem>
						))}
					</TextField>
					<Button
						variant="contained"
						color="secondary"
						startIcon={
							downloading ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<DownloadIcon />
							)
						}
						disabled={downloading}
						onClick={handleDownload}
					>
						{downloading ? "Preparing…" : "Download data pack"}
					</Button>
				</Box>

				{error && (
					<Alert severity="info" sx={{ marginTop: 3 }}>
						{error}
					</Alert>
				)}
			</Paper>
		</Box>
	);
};

export default DataPack;
